import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sparkles, 
  Copy, 
  Save, 
  RefreshCw,
  Facebook,
  Instagram,
  Search,
  Mail,
  FileText,
  Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatIcons = {
  facebook: Facebook,
  instagram: Instagram,
  google: Search,
  email: Mail,
  landing_page: FileText
};

const formatLabels = {
  facebook: "Facebook",
  instagram: "Instagram", 
  google: "Google Ads",
  email: "Email",
  landing_page: "Landing Page"
};

const modelLabels = {
  "gpt-5.2": "GPT-5.2",
  "claude-sonnet-4-5": "Claude Sonnet",
  "gemini-3-flash": "Gemini Flash"
};

export default function AdGenerator() {
  const [selectedFormat, setSelectedFormat] = useState("facebook");
  const [selectedModel, setSelectedModel] = useState("gpt-5.2");
  const [targetAudience, setTargetAudience] = useState("Virginia homeowners looking to reduce electricity bills");
  const [keyBenefits, setKeyBenefits] = useState("Zero upfront cost, Lower monthly bills, Clean energy");
  const [tone, setTone] = useState("professional");
  const [includeTestimonial, setIncludeTestimonial] = useState(true);
  const [generatedAd, setGeneratedAd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");

  const generateAd = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ads/generate`, {
        ad_format: selectedFormat,
        ai_model: selectedModel,
        target_audience: targetAudience,
        key_benefits: keyBenefits,
        tone: tone,
        include_testimonial: includeTestimonial
      });
      setGeneratedAd(response.data);
      toast.success("Ad generated successfully!");
    } catch (error) {
      console.error("Error generating ad:", error);
      toast.error("Failed to generate ad. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!generatedAd) return;
    
    const text = `${generatedAd.headline}\n\n${generatedAd.body}\n\n${generatedAd.call_to_action}${
      generatedAd.hashtags?.length ? `\n\n${generatedAd.hashtags.join(" ")}` : ""
    }`;
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const saveAsTemplate = async () => {
    if (!generatedAd || !templateName.trim()) return;
    
    try {
      await axios.post(`${API}/ads/${generatedAd.id}/save?name=${encodeURIComponent(templateName)}`);
      toast.success("Saved as template!");
      setSaveDialogOpen(false);
      setTemplateName("");
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="ad-generator-page">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-slate-900">Ad Generator</h1>
        <p className="text-sm md:text-base text-slate-500 mt-1">Create compelling solar PPA ads with AI assistance</p>
      </div>

      {/* Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left - Controls */}
        <div className="space-y-6">
          {/* AI Model Selection */}
          <Card className="border-slate-100" data-testid="model-selection-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#A94442]" />
                AI Model
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(modelLabels).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setSelectedModel(value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedModel === value
                        ? "bg-[#A94442] text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    data-testid={`model-btn-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Ad Format Selection */}
          <Card className="border-slate-100" data-testid="format-selection-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Outfit'] text-lg">Ad Format</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 md:gap-3">
                {Object.entries(formatLabels).map(([value, label]) => {
                  const Icon = formatIcons[value];
                  return (
                    <button
                      key={value}
                      onClick={() => setSelectedFormat(value)}
                      className={`p-3 md:p-4 rounded-xl border-2 transition-all text-center ${
                        selectedFormat === value
                          ? "border-[#A94442] bg-[#A94442]/5"
                          : "border-slate-100 hover:border-slate-200"
                      }`}
                      data-testid={`format-btn-${value}`}
                    >
                      <Icon className={`w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2 ${
                        selectedFormat === value ? "text-[#A94442]" : "text-slate-400"
                      }`} />
                      <span className={`text-xs md:text-sm font-medium ${
                        selectedFormat === value ? "text-[#A94442]" : "text-slate-600"
                      }`}>
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Ad Settings */}
          <Card className="border-slate-100" data-testid="ad-settings-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Outfit'] text-lg">Ad Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="target" className="text-slate-600">Target Audience</Label>
                <Input
                  id="target"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="Describe your target audience"
                  className="mt-1.5 h-11"
                  data-testid="target-audience-input"
                />
              </div>

              <div>
                <Label htmlFor="benefits" className="text-slate-600">Key Benefits</Label>
                <Textarea
                  id="benefits"
                  value={keyBenefits}
                  onChange={(e) => setKeyBenefits(e.target.value)}
                  placeholder="List key benefits to highlight"
                  className="mt-1.5 min-h-[80px]"
                  data-testid="key-benefits-input"
                />
              </div>

              <div>
                <Label htmlFor="tone" className="text-slate-600">Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="educational">Educational</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between py-2">
                <Label htmlFor="testimonial" className="text-slate-600">Include Testimonial</Label>
                <Switch
                  id="testimonial"
                  checked={includeTestimonial}
                  onCheckedChange={setIncludeTestimonial}
                  data-testid="testimonial-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Generate Button */}
          <Button
            onClick={generateAd}
            disabled={loading}
            className="w-full h-12 md:h-14 text-base md:text-lg font-semibold bg-[#A94442] hover:bg-[#8B3836] rounded-full"
            data-testid="generate-ad-btn"
          >
            {loading ? (
              <>
                <div className="spinner mr-3" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Generate Ad
              </>
            )}
          </Button>
        </div>

        {/* Right - Preview */}
        <div className="space-y-6">
          <Card className="border-slate-100 min-h-[500px]" data-testid="ad-preview-card">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="font-['Outfit'] text-lg">
                Ad Preview
                {generatedAd && (
                  <span className="ml-2 text-sm font-normal text-slate-500">
                    ({formatLabels[generatedAd.ad_format]})
                  </span>
                )}
              </CardTitle>
              {generatedAd && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyToClipboard}
                    className="gap-2"
                    data-testid="copy-ad-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2" data-testid="save-template-btn">
                        <Save className="w-4 h-4" />
                        Save
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-['Outfit']">Save as Template</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label htmlFor="templateName">Template Name</Label>
                          <Input
                            id="templateName"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="e.g., Facebook PPA Promo"
                            className="mt-1.5"
                            data-testid="template-name-input"
                          />
                        </div>
                        <Button 
                          onClick={saveAsTemplate}
                          className="w-full bg-[#A94442] hover:bg-[#8B3836]"
                          data-testid="confirm-save-btn"
                        >
                          Save Template
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {generatedAd ? (
                <div className="space-y-6 animate-fade-in">
                  {/* Model Badge */}
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-full bg-slate-100 text-xs font-medium text-slate-600">
                      Generated with {modelLabels[generatedAd.ai_model]}
                    </span>
                  </div>

                  {/* Headline */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Headline</Label>
                    <p className="mt-2 text-xl font-['Outfit'] font-bold text-slate-900">
                      {generatedAd.headline}
                    </p>
                  </div>

                  {/* Body */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Body</Label>
                    <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {generatedAd.body}
                    </p>
                  </div>

                  {/* CTA */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Call to Action</Label>
                    <div className="mt-2">
                      <span className="inline-block px-6 py-3 bg-[#A94442] text-white rounded-full font-semibold">
                        {generatedAd.call_to_action}
                      </span>
                    </div>
                  </div>

                  {/* Hashtags */}
                  {generatedAd.hashtags && generatedAd.hashtags.length > 0 && (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-400">Hashtags</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {generatedAd.hashtags.map((tag, index) => (
                          <span 
                            key={index}
                            className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Regenerate */}
                  <Button
                    variant="outline"
                    onClick={generateAd}
                    disabled={loading}
                    className="w-full mt-4 gap-2"
                    data-testid="regenerate-ad-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    Regenerate
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[400px] text-center">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="font-['Outfit'] font-semibold text-lg text-slate-700 mb-2">
                    No Ad Generated Yet
                  </h3>
                  <p className="text-slate-500 max-w-sm">
                    Configure your ad settings on the left and click "Generate Ad" to create AI-powered marketing copy.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
